if (!customElements.get('lookbook-modal')) {
  // TODO: Update `i` URL param on open and clear on close
  class LookbookModal extends ModalDialog {
    constructor() {
      super();
      this.slider = this.querySelector('[data-lookbook-media-slider]');
      this.list = this.querySelector('[data-lookbook-media-list]');
      this.details = this.querySelector('[data-lookbook-modal-details]');
      this.detailsSlider = this.querySelector('[data-lookbook-details-slider]');
      this.footer = this.querySelector('[data-lookbook-modal-footer]');
      this.thumbnails = this.querySelector('[data-lookbook-thumbnails]');
      this.background = this.querySelector('[data-background]');
      this.connectedGrid = document.querySelector('[data-lookbook-grid]');
      this.shareContainer = this.querySelector('[data-share]');
      this.overlay = null;
      this.mql = window.matchMedia('(min-width: 750px)');

      this.handleKeyPress = throttle(this.onKeyPress.bind(this), 100);
      this.debouncedOnSlideChanged = debounce(
        this.onSlideChanged.bind(this),
        SLIDE_CHANGE_DEBOUNCE_TIMER
      );

      this.transition = {
        duration: 0.5,
        ease: 'power2.inOut',
        easeIn: 'power1.in',
        easeOut: 'power1.out',
      };
    }

    connectedCallback() {
      super.connectedCallback();
      this.slider.addEventListener('slideChanged', this.debouncedOnSlideChanged);
      this.addEventListener('keydown', this.handleKeyPress);

      this.setActiveFromUrl();
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.slider.removeEventListener('slideChanged', this.debouncedOnSlideChanged);
      this.removeEventListener('keydown', this.handleKeyPress);
    }

    // TODO: Clean this up
    setActiveFromUrl() {
      const urlParams = new URLSearchParams(location.search);
      const activeItemId = urlParams.get('i');

      if (activeItemId) {
        const activeOpener = document.querySelector(`lookbook-modal-opener button[data-media-id="${activeItemId}"]`)?.closest('lookbook-modal-opener');

        if (activeOpener) {
          if (activeOpener.open) {
            activeOpener.open();
          } else {
            activeOpener.dataset.open = true;
          }
        }
      }
    }

    hide() {
      if (this.slider.isNavigating || this.scaleDownTimeline?.isActive()) return;

      super.hide();
    }

    show(opener, openerMedia) {
      this.setConnectedMedia(openerMedia);

      this.showActiveMedia(opener);

      super.show(opener);
    }

    onSlideChanged(e) {
      this.setActiveItem(e.detail.currentElement);
      this.updateConnection();
    }

    updateConnection() {
      this.setConnectedMedia(
        document.querySelector(
          `lookbook-modal-opener [data-flip-id="${this.activeMedia.dataset.flipId}"]`
        ),
        true
      );
    }

    setConnectedMedia(media, isOpen = false) {
      if (!media) {
        console.error(
          'Lookbook Modal: Failed to update connected media, provided element is invalid',
          media
        );
        return false;
      }

      this.connectedMedia = media;

      const connectedMediaContainer = this.connectedMedia.parentElement;
      const connectedMediaBounds = connectedMediaContainer.getBoundingClientRect();
      // TODO: Delay til after scroll if opening
      if (!isInViewport(connectedMediaContainer, { bounds: connectedMediaBounds, partial: 0.1 })) {
        console.debug(
          'Lookbook Modal: Connected media not in viewport. Scrolling...',
          this.connectedMedia
        );
        const offset = parseFloat(
          getComputedStyle(this.connectedGrid).getPropertyValue('row-gap') || 0
        );
        smoothScroll(connectedMediaContainer, {
          offset,
          targetBounds: connectedMediaBounds,
          alignToClosestEdge: true,
          jump: isOpen,
        });
      }
    }

    floatElements(float = true) {
      this.slider.lockAll();
      // TODO: Add toggleStyles function to utils based on this
      gsap.set(
        [this.details, this.footer],
        float ? { position: 'absolute' } : { clearProps: 'position' }
      );

      if (this.mql.matches) {
        gsap.set(
          this.activeMedia.parentElement,
          float ? { paddingRight: this.details.offsetWidth } : { clearProps: 'paddingRight' }
        );
        gsap.set(
          [this.details, this.list],
          float ? { paddingBottom: this.footer.offsetHeight } : { clearProps: 'paddingBottom' }
        );
      } else {
        gsap.set(
          this.details,
          float ? { top: this.activeMedia.offsetHeight } : { clearProps: 'top' }
        );
        gsap.set(
          this.activeMedia.parentElement,
          float
            ? { paddingBottom: window.innerHeight - this.activeMedia.offsetHeight }
            : { clearProps: 'paddingBottom' }
        );
      }

      setTimeout(() => {
        this.slider.releaseAll();
      }, 100);
    }

    resetMedia() {
      gsap.set([this.activeMedia, this.connectedMedia], { clearProps: 'display,transform' });
    }

    resetLayout() {
      this.resetMedia();
      this.floatElements(false);
    }

    createState() {
      gsap.set(this.activeMedia, { display: 'none' });
      this.flipState = Flip.getState([this.connectedMedia, this.activeMedia]);

      this.setAttribute('open', true);
      gsap.set(this.activeMedia, { display: 'block' });
      gsap.set(this.connectedMedia, { display: 'none' });
    }

    scaleUpMedia() {
      if (this.scaleUpTimeline) this.scaleUpTimeline.progress(1).kill();

      const { duration, ease, easeOut } = this.transition;
      const fadeElements =
        this.detailsSlider.sliderItemsToShow[this.activeIndex].querySelectorAll('[data-fade-in]');

      this.createState();

      this.floatElements(true);
      this.scaleUpTimeline = Flip.from(this.flipState, {
        targets: this.activeMedia,
        duration,
        ease,
        simple: true,
        scale: true,
        onStart: this.handleTimelineStart,
        onInterrupt: () => {
          this.resetLayout();
        },
      });

      // STAGGERED
      this.scaleUpTimeline.fromTo(
        [this.details, this.footer, this.background],
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: duration / 2,
          ease: easeOut,
          onComplete: () => {
            this.resetLayout();
          },
        },
        `-=${duration / 2}`
      );

      if (fadeElements.length) {
        this.scaleUpTimeline.fromTo(
          fadeElements,
          { autoAlpha: 0 },
          {
            autoAlpha: 1,
            duration,
            ease: 'power.in',
            stagger: {
              each: 0.05,
              ease: 'power1.in',
              grid: 'auto',
              axis: 'y',
            },
          },
          `-=${duration / 4}`
        );
      }

      // UNIFORM
      // scaleUp
      //   .fromTo(
      //     [this.details, this.footer, this.background],
      //     { autoAlpha: 0 },
      //     {
      //       autoAlpha: 1,
      //       duration,
      //       ease,
      //     },
      //     '0'
      //   );
    }

    scaleDownMedia() {
      if (this.scaleDownTimeline) this.scaleDownTimeline.progress(1).kill();

      const { duration, ease, easeIn } = this.transition;

      this.scaleUpTimeline?.kill();
      this.floatElements(true);
      this.scaleDownTimeline = gsap.timeline({
        onStart: () => {
          this.connectedMedia.style.display = 'none';
        },
        onComplete: this.handleTimelineReverseComplete,
      });

      // Staggered
      this.scaleDownTimeline
        .fromTo(
          [this.details, this.footer, this.background],
          { autoAlpha: 1 },
          {
            autoAlpha: 0,
            duration: duration / 2,
            ease: easeIn,
          }
        )
        .add(
          Flip.fit(this.activeMedia, this.connectedMedia, {
            duration,
            ease,
            simple: true,
            scale: true,
            overwrite: true,
            onComplete: this.resetLayout.bind(this),
          }),
          '<'
        );
    }

    updateAttributes(open = true, stateChange = false) {
      super.updateAttributes(open, stateChange);

      if (stateChange && !open) {
        gsap.set(this.connectedMedia, { clearProps: 'display' });
      }
    }

    buildTimeline() {}

    animate(open = true) {
      if (open) {
        this.scaleUpMedia();
      } else {
        this.scaleDownMedia();
      }
    }

    onKeyPress(e) {
      if (!this.isOpen()) return;

      switch (e.key) {
        case 'ArrowLeft':
          this.slider.step(-1);
          break;
        case 'ArrowRight':
          this.slider.step(1);
          break;
      }
    }

    setActiveItem(item) {
      this.activeItem = item;
      this.activeMedia = this.activeItem.querySelector('[data-flip-id]');
      this.activeIndex = Array.from(this.list.children).indexOf(this.activeItem);

      this.updateShareButton();
    }

    showActiveMedia(opener) {
      const activeItem = this.slider.querySelector(
        `[data-media-id="${opener.getAttribute('data-media-id')}"]`
      );

      if (activeItem === this.activeItem) return;

      this.setActiveItem(activeItem);

      this.slider.goToSlide(this.activeIndex, { jump: true });
      this.detailsSlider.goToSlide(this.activeIndex, { jump: true });
      this.thumbnails.setActiveThumbnail(this.activeIndex);
    }

    // TODO: Update to use attributes instead of template
    // TODO: --> Remove <template> and apply as attributes
    // TODO: --> Capture attributes and update `share-button` attributes
    updateShareButton() {
      const shareTemplate = document.getElementById(
        `LookbookShareTemplate-${this.activeItem.dataset.mediaId}`
      );
      const shareContent = shareTemplate.content.cloneNode(true);
      // const shareLinks = Array.from(shareTemplate.content.querySelectorAll('[data-share-link]'));

      // const pinIt = this.shareContainer.querySelector('pin-it');
      // if (pinIt) {
      //   ['pin-url', 'pin-description', 'pin-media'].forEach(attr => {
      //     if (shareLinks[0].hasAttribute(attr)) {
      //       pinIt.setAttribute(attr, shareLinks[0].getAttribute(attr));
      //     }
      //   });
      //   pinIt.setAttribute('pin-url', shareLinks[0].getAttribute('pin-url'));
      //   pinIt.setAttribute('pin-description', shareLinks[0].getAttribute('pin-description'));
      //   pinIt.setAttribute('pin-media', shareLinks[0].getAttribute('pin-media'));
      // }

      while (this.shareContainer.firstChild) {
        this.shareContainer.removeChild(this.shareContainer.firstChild);
      }

      this.shareContainer.appendChild(shareContent);
    }
  }
  customElements.define('lookbook-modal', LookbookModal);

  class LookbookModalOpener extends ModalOpener {
    constructor() {
      super();
      this.media = this.querySelector('[data-flip-id]');
      this.modal = document.querySelector(this.getAttribute('data-modal'));
      this.connectedMedia = this.modal.querySelector(
        `[data-flip-id="${this.media.dataset.flipId}"]`
      );
      this.observeIntent = parseBooleanAttribute(this.getAttribute('observe'));
      this.handleOpenIntent = this.onOpenIntent.bind(this);
      this.stickyHeader = document.querySelector('sticky-header');
    }

    connectedCallback() {
      super.connectedCallback();

      if (this.dataset.preload) {
        this.loadConnectedMedia();
      }

      if (this.observeIntent) {
        this.intentListener = hoverintent(this, this.handleOpenIntent, () => {});
        this.intentListener.options({ sensitivity: 20, interval: 25 });
      }

      if (this.dataset.open) {
        this.open();
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      this.intentListener?.remove();
    }

    loadConnectedMedia() {
      this.connectedMedia.loading = 'eager';
      this.intentListener?.remove();
    }

    onOpenIntent(e) {
      this.loadConnectedMedia();
    }

    open() {
      if (this.stickyHeader.isVisible()) {
        this.stickyHeader.hide();
        setTimeout(() => this.modal?.show(this.button, this.media), 250);
      } else {
        this.modal?.show(this.button, this.media);
      }
    }
  }
  customElements.define('lookbook-modal-opener', LookbookModalOpener);
}
